import { Activity, Clock, Zap, DollarSign, AlertTriangle, Info } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ActivityEvent } from '../api/client';

interface ActivityListProps {
  events: ActivityEvent[];
}

const eventTypeConfig: Record<string, { color: string; icon: typeof Activity }> = {
  optimization: {
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: Zap,
  },
  price_update: {
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: DollarSign,
  },
  telemetry: {
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: Activity,
  },
  alert: {
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: AlertTriangle,
  },
  status: {
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    icon: Info,
  },
};

export function ActivityList({ events }: ActivityListProps) {
  if (events.length === 0) {
    return (
      <div className="card p-6 text-center">
        <Activity className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto" />
        <p className="mt-2 text-gray-500 dark:text-gray-400">No recent activity</p>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Events will appear here when sites are active
        </p>
      </div>
    );
  }

  return (
    <div className="card divide-y divide-gray-100 dark:divide-gray-700">
      <div className="px-4 py-3 bg-gray-50 dark:bg-brand-navy-light rounded-t-lg">
        <h3 className="font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
      </div>
      {events.map((event) => {
        const config = eventTypeConfig[event.event_type] || eventTypeConfig.status;
        const IconComponent = config.icon;

        return (
          <div key={event.id} className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-brand-navy-light rounded-full">
                <IconComponent className="w-4 h-4 text-brand-orange" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {event.message}
                </p>
                {event.site_name && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Site: {event.site_name}
                  </p>
                )}
              </div>
            </div>
            <div className="text-right">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.color}`}
              >
                {event.event_type.replace('_', ' ')}
              </span>
              <div className="flex items-center gap-1 mt-1 text-xs text-gray-400 dark:text-gray-500">
                <Clock className="w-3 h-3" />
                <span>
                  {formatDistanceToNow(new Date(event.timestamp), {
                    addSuffix: true,
                  })}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
